import React, { useState } from 'react';

/**
 * The identity the platform stamped, shown rather than asserted.
 *
 * <h3>Why this is a step</h3>
 *
 * Stage 02's definition is "SPIFFE/SVID issued; cryptographic name stamped".
 * Protect used to cover it with one sentence of narration and two steps that
 * actually belong to Register — minting a gateway key and copying a snippet.
 * A gateway key is an authentication credential for the registration; it is not
 * the agent's name. The name, the trust domain it belongs to and the claims
 * carried in the SVID extension are the evidence, and they are what the
 * platform's own Identity tab shows.
 *
 * <h3>Honest about the certificate</h3>
 *
 * The name is minted at registration; the X.509 SVID is issued when the
 * workload first presents itself, so a freshly registered agent comes back with
 * `svidActive: false`. That is a normal state, not a failure. It is reported as
 * "not presented yet" — neither dressed up as provisioned nor rendered as an
 * error, both of which would mislead somebody deciding whether to trust it.
 *
 * <h3>Nothing is asked</h3>
 *
 * Identity is not a decision. The platform assigned it; this step puts it in
 * front of the person on the way past, which is the only point at which anyone
 * ever looks at a SPIFFE name.
 */
export default function IdentityAsk({ identity, onAnswer, busy = false }) {
  const [copied, setCopied] = useState(false);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      /* A browser that refuses the clipboard is not a failure of the step —
         the name is on screen and can be selected. */
      setCopied(false);
    }
  };

  /* Said out loud. A blank panel reads as "this agent has no identity", which
     is a different and much worse claim than "we could not read it". */
  if (identity?.error || !identity?.spiffeId) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
        <p style={NOTE}>
          {identity?.error
            || 'Its identity is not readable from here yet. It is on the agent’s own page '
             + 'in the full platform.'}
        </p>
        <div>
          <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                  onClick={() => onAnswer({ seen: true, spiffeId: null })}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  const { claims = {} } = identity;
  const rows = [
    ['Kind', claims.kind],
    ['Autonomy', claims.autonomy],
    ['Risk tier', claims.riskTier],
    ['Policies', claims.policyCount == null ? null : String(claims.policyCount)],
    ['Platform', claims.platformVersion],
  ].filter(([, v]) => v);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      <p style={NOTE}>
        No other agent carries this name. Every call it makes presents it, and every line of
        its record is written against it.
      </p>

      <div style={PANEL}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={CAPTION}>SPIFFE name</span>
          <button type="button" className="p7-btn p7-btn--sm" style={{ marginLeft: 'auto' }}
                  onClick={() => copy(identity.spiffeId)}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <code className="p7-mono"
              style={{ display: 'block', marginTop: '6px', wordBreak: 'break-all',
                       fontSize: '12px', color: 'var(--p7-ink)' }}>
          {identity.spiffeId}
        </code>
        <div style={{ marginTop: '9px', display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
          <Fact label="Trust domain" value={identity.trustDomain} mono />
          {/* The certificate, said the way it actually is. */}
          <Fact label="Certificate"
                value={identity.svidActive ? 'Active' : 'Not presented yet'}
                note={identity.svidActive
                  ? null
                  : 'Issued the first time the agent presents itself.'} />
          {identity.svidActive && identity.svidSerial
            ? <Fact label="Serial" value={identity.svidSerial} mono /> : null}
          {identity.svidActive && identity.svidExpiresAt
            ? <Fact label="Expires" value={identity.svidExpiresAt} mono /> : null}
        </div>
      </div>

      {rows.length ? (
        <div style={PANEL}>
          <span style={CAPTION}>Carried in the certificate</span>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap',
                        gap: '6px 18px' }}>
            {rows.map(([label, value]) => <Fact key={label} label={label} value={value} />)}
          </div>
        </div>
      ) : null}

      <div>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                onClick={() => onAnswer({ seen: true, spiffeId: identity.spiffeId,
                                          svidActive: !!identity.svidActive })}>
          Continue
        </button>
      </div>
    </div>
  );
}

function Fact({ label, value, mono, note }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
      <span style={CAPTION}>{label}</span>
      <span className={mono ? 'p7-mono' : undefined}
            style={{ font: 'var(--p7-text-body)',
                     fontFamily: mono ? undefined : 'var(--p7-font-sans)',
                     fontSize: mono ? '12px' : undefined,
                     color: 'var(--p7-ink)' }}>
        {value}
      </span>
      {note ? <span style={{ ...NOTE, fontSize: '11px' }}>{note}</span> : null}
    </span>
  );
}

const NOTE = {
  margin: 0, font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
  color: 'var(--p7-ink-subtle)', lineHeight: 1.5,
};

const CAPTION = {
  font: '600 10.5px/16px var(--p7-font-sans)', letterSpacing: '.08em',
  textTransform: 'uppercase', color: 'var(--p7-ink-subtle)',
};

const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '11px 13px', background: 'var(--p7-surface-sunken)',
};
